import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from "react-toastify";
import { Grid, Box, Typography, } from '@mui/material';
import { CustomReactSelect, CustomButton } from '../../shared';
import { createPartnerConnection, updatePartnerConnection, getPartnerConnections, deletePartnerConnection } from '../../../redux/thunks/partnerThunk';
import { updateCampaign } from '../../../redux/thunks/campaignThunks';
import { selectCampaignConnectionOptions } from '../../../redux/reducers/partnerReducer';

const initialValue =  {
    connectionOption: "",
    connectionName: "",
    host: "",
    sftpUser: "",
    sftpPassword: "",
    sftpPath: "",
    sftpArchivePath: "",
};

const sftpConnection = yup.object().shape({
    connectionOption: yup.string().required('Please Select Connection Name'),
    connectionName: yup.string().required('Connection Name is required'),
    host: yup.string().required('Host is required'),
    sftpUser: yup.string().required('User is required'),
    sftpPassword: yup.string().when("connectionOption", {
        is: (val) => {
            return val.trim() === "";
        },
        then: () => yup.string().required('SFTP password is required'),
        otherwise: () => yup.string().notRequired(),
    }),
    sftpPath: yup.string().required('SFTP Path is required'),
    sftpArchivePath: yup.string().required('SFTP Archive Path is required'),
    selectedInCSP: yup.number().default(0),
});

function SFTPConnection(props) {
    const { partnerId, handleCampaignData } = props;
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);
    const connections = useSelector(selectCampaignConnectionOptions(partnerId));
    
    const { register, 
        handleSubmit, 
        setValue, 
        getValues, 
        formState: { errors }, 
        control,
        reset, 
    } = useForm({ resolver: yupResolver(sftpConnection), defaultValues: initialValue });
    
    const { field: { value: connectionOption, onChange: onConnectionOptionChange, ...restConnectionOptionProps }, } = useController({ name: 'connectionOption', control });
    
    const onSubmit = (data) => {
        const campaignsData = handleCampaignData();
        // Update Campaign 
        let updatedCampaignData = {
            auto_scheduler: campaignsData.auto_scheduler,
            estimated_duration: campaignsData.estimated_duration,
            hour: campaignsData.hour,
            name: campaignsData.name,
            partner_connection_id: data.connectionOption,
            partner_id: partnerId,               
            schedule: campaignsData.schedule,
            targeted_duration: campaignsData.targeted_duration,
            triggers: campaignsData.triggers,
            type: campaignsData.type,
        };
        if(data.connectionOption.trim() === ""){
            // New Partner Connection
            let newSFTPConnectionData = {
                id : null,
                name: data.connectionName,
                partner_id: partnerId,
                sftp_archive_path: data.sftpArchivePath,
                sftp_host: data.host,
                sftp_password: data.sftpPassword,
                sftp_path: data.sftpPath,
                sftp_user: data.sftpUser,
            }
            dispatch(createPartnerConnection({companyId: company.id, data: newSFTPConnectionData})).then((result) => {
                if(result.payload.status_code === 200){
                    updatedCampaignData.partner_connection_id = result.payload.connection.id;
                    dispatch(updateCampaign({companyId: company.id, campaignId: campaign.id, data: updatedCampaignData}));
                    dispatch(getPartnerConnections({companyId: company.id}));
                    let option = {    
                        label: result.payload.connection.name,
                        value: result.payload.connection.id,
                        connection: result.payload.connection,
                    }
                    handleConnectionChange(option);
                };
                
            }).catch((error) => {
                console.error("createPartnerConnection >> error", error);
                toast.error(error);
            });
        } else {
            // Update Partner Connection
            let updatedSFTPConnectionData = {
                company_id: company.id,
                id : data.connectionOption,
                name: data.connectionName,
                partner_id: partnerId,
                sftp_archive_path: data.sftpArchivePath,
                sftp_host: data.host,
                sftp_path: data.sftpPath,
                sftp_user: data.sftpUser,
                selected_in_csp: data.selectedInCSP, 
            };

            if(data.sftpPassword !== ""){
                updatedSFTPConnectionData['sftp_password'] = data.sftpPassword;
            }

            dispatch(updatePartnerConnection({companyId: company.id, partnerConnectionId: data.connectionOption, data: updatedSFTPConnectionData})).then(result=>{
                if(result.payload.status_code === 200){
                    dispatch(updateCampaign({companyId: company.id, campaignId: campaign.id, data: updatedCampaignData}));
                }
            });
            
        } 
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleConnectionChange = (selectedOption) => {
        onConnectionOptionChange(selectedOption ? selectedOption.value : selectedOption);

        if(Object.values(selectedOption.connection).length !== 0) {
            setValue('connectionName', selectedOption.connection.name);
            setValue('host', selectedOption.connection.sftp_host);
            setValue('sftpUser', selectedOption.connection.sftp_user);
            setValue('sftpPath', selectedOption.connection.sftp_path);
            setValue('sftpArchivePath', selectedOption.connection.sftp_archive_path);
            setValue('selectedInCSP', selectedOption.connection.selected_in_csp);
        }
    };

    const handleRemoveConnection = (value, label) => {
        dispatch(deletePartnerConnection({ companyId: company.id, connectionId: value })).then(result => {
            if (result.payload.status_code === 200) {
                dispatch(getPartnerConnections({ companyId: company.id }));
                if(value === getValues('connectionOption')){
                    reset();
                }
            }
        })
    };

    useEffect(()=> {
        if(campaign && campaign.partner_connection){
            setValue('connectionOption', campaign.partner_connection_id);
            setValue('connectionName', campaign.partner_connection.name);
            setValue('host', campaign.partner_connection.sftp_host);
            setValue('sftpUser', campaign.partner_connection.sftp_user);
            setValue('sftpPath', campaign.partner_connection.sftp_path);
            setValue('sftpArchivePath', campaign.partner_connection.sftp_archive_path);
            setValue('selectedInCSP', campaign.partner_connection.selected_in_csp);
        }
    },[]);

    return (
        <Grid container>
            <form id="sftpConnection" onSubmit={handleSubmit(onSubmit)} className="w-100">
                <Grid item xs={12}>
                    {/* Partner SFTP Connection */}
                    <Box className="padding-24px pt-0">
                        <Box className="pb-24px">
                            <Typography className="text-md-semi-bold gray-900">Partner SFTP Connection</Typography>
                        </Box>
                        <Grid container spacing="12px">
                            {/* Select Connection Name */}
                            <Grid item xs={6}>
                                <CustomReactSelect
                                    key={ connections }
                                    options={connections}
                                    placeholder="Select Connection Name"
                                    handleFilter={(option) => handleConnectionChange(option)}                                  
                                    selectedOption={
                                        connectionOption
                                            ? connections.find(
                                                (x) => x.value === connectionOption
                                            )
                                            : connectionOption
                                    }
                                    formatOptionLabel={({ value, label, }) => (
                                        <div className='d-flex align-items-center'>
                                            <div className='flex-1'>{label}</div>
                                            {label != "New Connection" ?
                                                <div onClick={() => handleRemoveConnection(value, label)} className='select-remove-button d-flex justify-content-center align-items-center' >x</div>
                                                : null}
                                        </div>
                                    )}
                                    {...restConnectionOptionProps}
                                />
                                <span className="text-xs-regular error-500">
                                    {errors.connectionOption?.message}
                                </span>
                            </Grid>
                            {/* Connection Name */}
                            <Grid item xs={6}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="text"
                                        placeholder="Connection Name"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('connectionName')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.connectionName?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* Host */}
                            <Grid item xs={6}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="text"
                                        placeholder="Host"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('host')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.host?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* User */}
                            <Grid item xs={6}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="text"
                                        placeholder="SFTP User"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        autoComplete="off"
                                        {...register('sftpUser')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.sftpUser?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* Password */}
                            <Grid item xs={6}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="password"
                                        placeholder="SFTP Password"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        autoComplete="new-password"
                                        {...register('sftpPassword')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.sftpPassword?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* SFTP Path */}
                            <Grid item xs={6}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="text"
                                        placeholder="SFTP Path"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('sftpPath')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.sftpPath?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* SFTP Archive Path */}
                            <Grid item xs={6}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="text"
                                        placeholder="SFTP Archive Path"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('sftpArchivePath')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.sftpArchivePath?.message}
                                    </span>
                                </Box>
                            </Grid>
                        </Grid>
                        <Box className="pt-12px pb-24px">
                            <CustomButton
                                title="Save"
                                titleColor="#FFFFFF"
                                buttonColor="#AD509E"
                                handleClick={handleSave}
                                type="submit"
                            />
                        </Box>
                    </Box>
                    
                </Grid>
            </form>
        </Grid>
    );
}

SFTPConnection.defaultProps = {
    handleCampaignData: () => {},
};

export default SFTPConnection;
